import React, { useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';

import Button from '@components/Common/Button';
import DatePicker from '@components/Common/DatePicker';
import Form from '@components/Common/Form';
import FormRow from '@components/Common/FormRow';
import FormSection from '@components/Common/FormSection';
import Input from '@components/Common/Input';
import Postcode from '@components/Common/Postcode';
import ConfirmModal from '@components/Modals/ConfirmModal';

import { GroupFormInputs } from '@type/group';

import { useClientCreate } from '@hooks/api/master/useClientAPIs';

import { titleStyle } from '@styles/common';

import {
  addressValidateOptions,
  bizNoValidateOptions,
  companyCeoValidateOptions,
  companyNameValidateOptions,
  corporateNoValidateOptions,
  dateValidateOptions,
} from '@constants/validation';

import { toApiDate } from '@utils/date';
import { bizNumberFormat, corparteNumberFormat } from '@utils/utils';

/** 마스터 계정 > 회사 관리 > 등록하기 */
const ClientNew = () => {
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  /** 등록 API */
  const { mutate } = useClientCreate(setConfirmModalOpen);

  /** react-hook-form Init */
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<GroupFormInputs>();

  /** 법인번호 Input onChange Handler */
  const handleCorportateNoChange = (e: any, setValue: any, onChange: any) => {
    setValue(corparteNumberFormat(e.target.value));
    onChange(corparteNumberFormat(e.target.value));
  };

  /** 사업자번호 Input onChange Handler */
  const handleBizNoChange = (e: any, setValue: any, onChange: any) => {
    setValue(bizNumberFormat(e.target.value));
    onChange(bizNumberFormat(e.target.value));
  };

  const onConfirm: SubmitHandler<GroupFormInputs> = async () => {
    try {
      setConfirmModalOpen(true);
    } catch (error: any) {
      console.log('ERROR_CLIENT_CREATE_CONFIRM');
    }
  };

  const onSubmit: SubmitHandler<GroupFormInputs> = async ({
    name,
    ceo,
    corporateNo,
    bizNo,
    establishmentDate,
    address,
    addressDetail,
  }) => {
    try {
      mutate({
        name,
        ceo,
        corporateNo: String(corporateNo?.replaceAll('-', '')),
        bizNo: String(bizNo?.replaceAll('-', '')),
        establishmentDate: toApiDate(establishmentDate),
        address,
        addressDetail,
      });
    } catch (error: any) {
      console.log('ERROR_CLIENT_CREATE_SUBMIT');
    }
  };

  return (
    <>
      <div css={[titleStyle, { justifyContent: 'space-between' }]}>
        회사 등록하기
        <Button
          label="등록하기"
          theme="secondary"
          handleClick={handleSubmit(onConfirm)}
        />
      </div>

      <form onSubmit={handleSubmit(onConfirm)}>
        <Form>
          <FormRow>
            <FormSection label="회사명" required>
              <Input
                name="name"
                control={control}
                rules={companyNameValidateOptions}
                errors={errors}
                placeHolder="회사명을 입력해 주세요."
              />
            </FormSection>
            <FormSection label="대표자명" required>
              <Input
                name="ceo"
                control={control}
                rules={companyCeoValidateOptions}
                errors={errors}
                placeHolder="대표자명을 입력해 주세요."
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="법인번호" required>
              <Input
                name="corporateNo"
                control={control}
                rules={corporateNoValidateOptions}
                errors={errors}
                placeHolder="숫자만 입력해 주세요."
                maxLength={14}
                handleChange={handleCorportateNoChange}
              />
            </FormSection>
            <FormSection label="사업자번호" required>
              <Input
                name="bizNo"
                control={control}
                rules={bizNoValidateOptions}
                errors={errors}
                placeHolder="숫자만 입력해 주세요."
                maxLength={12}
                handleChange={handleBizNoChange}
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="주소" required>
              <Postcode
                name="address"
                errors={errors}
                control={control}
                rules={addressValidateOptions}
              />
            </FormSection>
            <FormSection label="상세주소" required>
              <Input
                name="addressDetail"
                control={control}
                rules={addressValidateOptions}
                errors={errors}
                placeHolder="상세주소를 입력해 주세요."
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="설립일" required>
              <DatePicker
                name="establishmentDate"
                errors={errors}
                control={control}
                rules={dateValidateOptions}
              />
            </FormSection>
          </FormRow>
        </Form>
      </form>

      {/* 등록하기 Confirm Modal */}
      {confirmModalOpen && (
        <ConfirmModal
          title="등록하시겠습니까?"
          buttonText={['취소하기', '등록하기']}
          open={confirmModalOpen}
          handleOpen={() => setConfirmModalOpen(false)}
          handleCancel={() => setConfirmModalOpen(false)}
          handleConfirm={handleSubmit(onSubmit)}
        />
      )}
    </>
  );
};

export default ClientNew;
